import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { canSeeMenuItem } from '../lib/capabilities.js';
import { useSession } from '../stores/session.js';
import { DomainApiError } from '../lib/api-error.js';
import { OrderReportCard } from '../components/order-report-card.js';
import {
  forceDeliverOrder,
  listOrderQueue,
  markAddonDelivered,
  refundOrder,
  rerunOrderRun,
  type OrderQueueItem,
  type OrderQueueItemLine,
} from '../lib/orders-api.js';

/**
 * `/objednavky-fronta` (krok C3) - fronta objednavek reportu, ktere cekaji
 * na kontrolu (stav `NEEDS_REVIEW`) nebo maji otevrenou attention polozku (SLA,
 * danovy doklad). Akce "dorucit i tak" / "vratit penize" jsou owner/admin
 * only (guard `commerce.order.force_deliver`, stejny vzor jako AdministracePage),
 * povinny duvod se zada pres `window.prompt` - minimalni UI, backend
 * (povinny duvod + audit) je zdroj pravdy, viz order-service.ts.
 */
export function ObjednavkyFrontaPage(): React.JSX.Element {
  const roles = useSession((s) => s.user?.roles ?? []);
  const allowed = canSeeMenuItem(roles, 'commerce.order.force_deliver');
  /**
   * Krok NAP11 (A25): zalozit objednavku reportu smi i ten, kdo frontu
   * provozovatele nespravuje - rozhoduje `commerce.order.create` (tentyz
   * grant, ktery autorizuje `POST /v1/orders`), ne opravneni „dorucit i tak".
   */
  const canOrder = canSeeMenuItem(roles, 'commerce.order.create');

  if (!allowed) {
    return (
      <div className="p-6" data-testid="objednavky-fronta-page">
        <h1 className="mb-4 text-xl font-bold">{t('webAdmin.ordersQueue.title', { locale: 'cs' })}</h1>
        {canOrder ? <OrderReportCard /> : null}
        {/*
          Hlaska o opravneni zustava i makleri, ktery objednat smi: tahle
          obrazovka je FRONTA provozovatele a ta mu dostupna opravdu neni
          (meri to `objednavky-fronta.spec.tsx`, krok C3).
        */}
        <p className="text-sm text-gray-500" role="alert">
          {t('webAdmin.ordersQueue.forbidden', { locale: 'cs' })}
        </p>
      </div>
    );
  }

  return (
    <>
      {canOrder ? (
        <div className="px-6 pt-6">
          <OrderReportCard />
        </div>
      ) : null}
      <OrdersQueueTable />
    </>
  );
}

/** Doplnky objednavky (hlavni polozka do fronty nepatri - ta je report sam). */
function addonsOf(order: OrderQueueItem): readonly OrderQueueItemLine[] {
  return (order.items ?? []).filter((item) => item.kind === 'ADDON');
}

/**
 * Krok UXD3b (nalez V7 z QA-UX 21. 10.) — STAV OBJEDNAVKY LIDSKY.
 *
 * Sloupec „Stav" prekladal jedine `NEEDS_REVIEW` a u vsech ostatnich stavu
 * tiskl SYROVY KOD (`AWAITING_PAYMENT`, `FULFILLING`). Lidska jmena stavu
 * objednavky uz JEDNO MISTO maji — katalog `webPublic.status.state.*`, ze
 * ktereho je cte stranka stavu objednavky na verejnem webu. Je to tyz
 * koncept (stav te same objednavky), takze se cte TENTYZ katalog; druhou
 * mapu v adminu nezakladame (pravidlo 2 CLAUDE.md §8). Neznamy stav
 * zustava jako kod — to je pravdivejsi nez vymyslet vetu.
 */
const ORDER_STATUS_KEY: Readonly<Record<string, MessageKey>> = {
  CREATED: 'webPublic.status.state.CREATED',
  AWAITING_PAYMENT: 'webPublic.status.state.AWAITING_PAYMENT',
  PAID: 'webPublic.status.state.PAID',
  FULFILLING: 'webPublic.status.state.FULFILLING',
  PENDING_RELEASE: 'webPublic.status.state.PENDING_RELEASE',
  DELIVERED: 'webPublic.status.state.DELIVERED',
  CANCELLED: 'webPublic.status.state.CANCELLED',
  REFUNDED: 'webPublic.status.state.REFUNDED',
  FAILED: 'webPublic.status.state.FAILED',
};

function statusLabel(status: string): string {
  const key = ORDER_STATUS_KEY[status];
  return key === undefined ? status : t(key, { locale: 'cs' });
}

function OrdersQueueTable(): React.JSX.Element {
  const [items, setItems] = React.useState<OrderQueueItem[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setItems(await listOrderQueue());
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.ordersQueue.error', { locale: 'cs' }));
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function handleForceDeliver(order: OrderQueueItem): Promise<void> {
    const reason = window.prompt(t('webAdmin.ordersQueue.reasonPromptForceDeliver', { locale: 'cs' }));
    if (reason === null) return;
    setBusyId(order.id);
    setError(null);
    setNotice(null);
    try {
      await forceDeliverOrder(order.id, reason);
      setNotice(t('webAdmin.ordersQueue.actionSuccess', { locale: 'cs' }));
      await load();
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.ordersQueue.actionError', { locale: 'cs' }));
    } finally {
      setBusyId(null);
    }
  }

  /**
   * Krok WEBF2: „doplnek dodan". Bez duvodu (na rozdil od „dorucit i tak" -
   * tam duvod obchazi kontrolu kvality, tady se jen hlasi hotova prace),
   * audit zapisuje server.
   */
  async function handleAddonDelivered(
    order: OrderQueueItem,
    productCode: string,
  ): Promise<void> {
    setBusyId(order.id);
    setError(null);
    setNotice(null);
    try {
      await markAddonDelivered(order.id, productCode);
      setNotice(t('webAdmin.ordersQueue.actionSuccess', { locale: 'cs' }));
      await load();
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.ordersQueue.actionError', { locale: 'cs' }));
    } finally {
      setBusyId(null);
    }
  }

  /**
   * Krok REV1 bod 2: SPADLY BEH ZNOVU. Stav behu fronta necte (hranice
   * kontextu), proto se tlacitko nabizi u kazde objednavky ve `FULFILLING` -
   * tedy presne u tech, ktere „se zpracovavaji" a pritom tu nejakou dobu
   * visi. Co uz bezi, server nezaradi dvakrat (`created: false`).
   */
  async function handleRerun(order: OrderQueueItem): Promise<void> {
    const reason = window.prompt(t('webAdmin.ordersQueue.reasonPromptRerun', { locale: 'cs' }));
    if (reason === null) return;
    if (reason.trim().length < 3) return;
    setBusyId(order.id);
    setError(null);
    setNotice(null);
    try {
      const result = await rerunOrderRun(order.id, reason);
      setNotice(
        t(
          result.created
            ? 'webAdmin.ordersQueue.actionSuccessRerun'
            : 'webAdmin.ordersQueue.actionRerunAlreadyQueued',
          { locale: 'cs' },
        ),
      );
      await load();
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.ordersQueue.actionError', { locale: 'cs' }));
    } finally {
      setBusyId(null);
    }
  }

  async function handleRefund(order: OrderQueueItem): Promise<void> {
    const reason = window.prompt(t('webAdmin.ordersQueue.reasonPromptRefund', { locale: 'cs' }));
    if (reason === null) return;
    setBusyId(order.id);
    setError(null);
    setNotice(null);
    try {
      await refundOrder(order.id, reason);
      setNotice(t('webAdmin.ordersQueue.actionSuccess', { locale: 'cs' }));
      await load();
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.ordersQueue.actionError', { locale: 'cs' }));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="p-6" data-testid="objednavky-fronta-page">
      <h1 className="mb-1 text-xl font-bold">{t('webAdmin.ordersQueue.title', { locale: 'cs' })}</h1>
      <p className="mb-4 text-sm text-gray-600">{t('webAdmin.ordersQueue.description', { locale: 'cs' })}</p>

      {error !== null && (
        <p role="alert" className="mb-2 text-red-600">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="mb-2 text-sm text-green-700" data-testid="orders-queue-notice">
          {notice}
        </p>
      )}

      {items === null ? (
        <p>{t('webAdmin.ordersQueue.loading', { locale: 'cs' })}</p>
      ) : items.length === 0 ? (
        <p data-testid="orders-queue-empty">{t('webAdmin.ordersQueue.empty', { locale: 'cs' })}</p>
      ) : (
        <table className="w-full text-sm" data-testid="orders-queue-table">
          <thead>
            <tr className="text-left">
              <th className="pr-4">{t('webAdmin.ordersQueue.columnOrderNumber', { locale: 'cs' })}</th>
              <th className="pr-4">{t('webAdmin.ordersQueue.columnAddress', { locale: 'cs' })}</th>
              <th className="pr-4">{t('webAdmin.ordersQueue.columnStatus', { locale: 'cs' })}</th>
              <th className="pr-4">{t('webAdmin.ordersQueue.columnReason', { locale: 'cs' })}</th>
              <th className="pr-4">{t('webAdmin.ordersQueue.columnAddons', { locale: 'cs' })}</th>
              <th className="pr-4">{t('webAdmin.ordersQueue.columnCreatedAt', { locale: 'cs' })}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((order) => (
              <tr key={order.id} className="border-t" data-testid={`orders-queue-row-${order.orderNumber}`}>
                <td className="pr-4 py-2">{order.orderNumber}</td>
                <td className="pr-4 py-2">{order.addressLabel}</td>
                <td className="pr-4 py-2">
                  {order.status === 'NEEDS_REVIEW'
                    ? t('webAdmin.ordersQueue.statusNeedsReview', { locale: 'cs' })
                    : statusLabel(order.status)}
                </td>
                <td className="pr-4 py-2">
                  {order.needsReviewReason ?? order.attentionReason ?? ''}
                  {/* Krok REV1: kolikaty pokus o beh - aby bylo videt, ze se to uz zkousel. */}
                  {(order.runAttempts ?? 1) > 1 ? (
                    <div className="text-gray-600" data-testid={`orders-queue-attempts-${order.orderNumber}`}>
                      {t('webAdmin.ordersQueue.attempts', { locale: 'cs', values: { count: order.runAttempts ?? 1 } })}
                    </div>
                  ) : null}
                </td>
                {/*
                  Krok WEBF2 (zadani bod 2): ruční doplneni LV/exekuci vidi,
                  KTERE DOPLNKY jsou objednane - jinak by provozovatel vedel
                  jen cenu a musel hadat, co ma obstarat.
                */}
                <td className="pr-4 py-2" data-testid={`orders-queue-addons-${order.orderNumber}`}>
                  {addonsOf(order).length === 0 ? (
                    '—'
                  ) : (
                    <ul>
                      {addonsOf(order).map((item) => (
                        <li key={item.productCode}>
                          {t(item.nameKey as MessageKey, { locale: 'cs' })}
                          {item.deliveredAt !== null ? (
                            <span data-testid={`orders-queue-addon-done-${item.productCode}`}>
                              {' '}
                              ✓
                            </span>
                          ) : item.fulfillment === 'MANUAL' ? (
                            <button
                              type="button"
                              className="rh-btn ml-2"
                              disabled={busyId === order.id}
                              onClick={() => void handleAddonDelivered(order, item.productCode)}
                              data-testid={`orders-queue-addon-deliver-${item.productCode}`}
                            >
                              {t('webAdmin.ordersQueue.actionAddonDelivered', { locale: 'cs' })}
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td className="pr-4 py-2">{order.createdAt}</td>
                <td className="py-2 whitespace-nowrap">
                  <button
                    type="button"
                    className="rh-btn rh-btn--primary mr-2"
                    disabled={busyId === order.id}
                    onClick={() => void handleForceDeliver(order)}
                    data-testid={`orders-queue-force-deliver-${order.orderNumber}`}
                  >
                    {t('webAdmin.ordersQueue.actionForceDeliver', { locale: 'cs' })}
                  </button>
                  {order.status === 'FULFILLING' ? (
                    <button
                      type="button"
                      className="rh-btn mr-2"
                      disabled={busyId === order.id}
                      onClick={() => void handleRerun(order)}
                      data-testid={`orders-queue-rerun-${order.orderNumber}`}
                    >
                      {t('webAdmin.ordersQueue.actionRerun', { locale: 'cs' })}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="rh-btn"
                    disabled={busyId === order.id}
                    onClick={() => void handleRefund(order)}
                    data-testid={`orders-queue-refund-${order.orderNumber}`}
                  >
                    {t('webAdmin.ordersQueue.actionRefund', { locale: 'cs' })}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
